import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { planPrice } from "../../utils/helperFuntions";
import { Icon, SectionHeader } from "../../components/public/ui";
import { smoothScrollToElement } from "../../components/public/smoothScroll";
import { useAuthModal } from "../../components/public/auth/AuthModalContext";

// Paid prices come from the same source the checkout uses
const price14 = planPrice(14);
const price30 = planPrice(30);
const price90 = planPrice(90);
const savings30 = Math.round((1 - price30 / ((price14 / 14) * 30)) * 100);

const freeFeatures = [
  { label: "1 Full-length mock test", included: true },
  { label: "10 AI-powered Writing & Speaking scores", included: true },
  { label: "Student dashboard & results", included: true },
  { label: "Unlimited AI evaluation", included: false },
];

const proFeatures = [
  "All CELPIP mock tests",
  "All 4 skills (L, R, W, S) unlocked",
  "Unlimited AI Speaking & Writing evaluation",
  "Student dashboard & results tracking",
  `Also available as a 14-day pass for $${price14}`,
];

const extendedFeatures = [
  "Everything in the 30-day pass",
  "90 days of unlimited access",
  "Lowest price per day",
  `60-day pass also available for $${planPrice(60)}`,
];

export const Pricing = () => {
  const pricingRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const { openAuth } = useAuthModal();

  // Other pages link here with navigate("/", { state: { scrollToPricing: true } })
  useEffect(() => {
    if (location.state?.scrollToPricing && pricingRef.current) {
      smoothScrollToElement(pricingRef.current);
    }
  }, [location]);

  useEffect(() => {
    window.pricingRef = pricingRef as React.RefObject<HTMLDivElement>;
  }, []);

  return (
    <section
      className="w-full py-20 bg-surface scroll-mt-20"
      id="pricing"
      ref={pricingRef}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionHeader
          eyebrow="Simple, Transparent Pricing"
          title="Choose the Right Preparation Plan for You"
          description="No subscription traps. Pay once for a pass and practice with full access until it ends."
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {/* Free */}
          <div className="bg-surface-container-lowest p-6 sm:p-8 rounded-3xl shadow-sm flex flex-col justify-between gap-8">
            <div className="flex flex-col gap-5">
              <span className="font-headline-sm text-headline-sm text-on-surface font-bold">
                Free Starter
              </span>
              <p className="font-body-sm text-body-sm text-on-surface-variant">
                Essential diagnostic practice for anyone getting started.
              </p>
              <div className="flex items-baseline gap-1">
                <span className="font-display-mobile text-display-mobile sm:font-display sm:text-display text-on-surface font-extrabold">
                  $0
                </span>
                <span className="text-on-surface-variant text-sm">
                  forever free
                </span>
              </div>
              <ul className="space-y-3 pt-4 text-xs text-on-surface list-none p-0 m-0">
                {freeFeatures.map((f) => (
                  <li
                    key={f.label}
                    className={`flex items-center gap-2.5 ${f.included ? "" : "text-on-surface-variant opacity-60"}`}
                  >
                    <Icon
                      name={f.included ? "check" : "close"}
                      className={`text-base ${f.included ? "text-tertiary" : ""}`}
                    />
                    <span>{f.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => openAuth("signup")}
              className="w-full py-3 rounded-xl bg-surface-container hover:bg-surface-container-high text-primary font-label-md text-label-md font-semibold transition-all text-center"
            >
              Get Started Free
            </button>
          </div>

          {/* 30-day pass (highlighted) */}
          <div className="bg-gradient-to-b from-surface-container-lowest to-surface-container-low p-6 sm:p-8 rounded-3xl shadow-2xl flex flex-col justify-between gap-8 relative transform lg:-translate-y-2">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-on-primary font-label-sm text-label-sm font-bold shadow-md">
              MOST POPULAR
            </div>
            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between">
                <span className="font-headline-sm text-headline-sm text-primary font-bold">
                  Student Essential
                </span>
                <span className="px-2.5 py-0.5 rounded bg-tertiary-fixed text-on-tertiary-fixed text-[11px] font-bold">
                  Save {savings30}%
                </span>
              </div>
              <p className="font-body-sm text-body-sm text-on-surface-variant">
                Complete preparation suite for serious test-takers aiming for
                CLB 9+.
              </p>
              <div className="flex items-baseline gap-1">
                <span className="font-display-mobile text-display-mobile sm:font-display sm:text-display text-primary font-extrabold">
                  ${price30}
                </span>
                <span className="text-on-surface-variant text-sm">
                  / 30-day pass
                </span>
              </div>
              <ul className="space-y-3 pt-4 text-xs text-on-surface font-medium list-none p-0 m-0">
                {proFeatures.map((f) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <Icon
                      name="check_circle"
                      className="text-primary text-base"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => openAuth("signup")}
              className="w-full py-3.5 rounded-xl bg-primary hover:bg-primary-container text-on-primary font-label-md text-label-md font-bold shadow-lg shadow-primary/25 transition-all hover:scale-[1.02]"
            >
              Unlock Full Access Now
            </button>
          </div>

          {/* 90-day pass */}
          <div className="bg-surface-container-lowest p-6 sm:p-8 rounded-3xl shadow-sm flex flex-col justify-between gap-8">
            <div className="flex flex-col gap-5">
              <span className="font-headline-sm text-headline-sm text-on-surface font-bold">
                Extended Prep
              </span>
              <p className="font-body-sm text-body-sm text-on-surface-variant">
                The longest access window for candidates with more time before
                test day.
              </p>
              <div className="flex items-baseline gap-1">
                <span className="font-display-mobile text-display-mobile sm:font-display sm:text-display text-on-surface font-extrabold">
                  ${price90}
                </span>
                <span className="text-on-surface-variant text-sm">
                  / 90-day pass
                </span>
              </div>
              <ul className="space-y-3 pt-4 text-xs text-on-surface list-none p-0 m-0">
                {extendedFeatures.map((f) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <Icon name="check" className="text-tertiary text-base" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => openAuth("signup")}
              className="w-full py-3 rounded-xl bg-surface-container hover:bg-surface-container-high text-primary font-label-md text-label-md font-semibold transition-all"
            >
              Get 90-Day Pass
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
